/** Deposits, withdrawals and swaps merged into one history list, with the filters of the Activity screen. */
import type {
  DepositInfo,
  DepositStatus,
  SwapInfo,
  SwapStatus,
  WithdrawalInfo,
  WithdrawalStatus,
} from "@custos/api-client";
import type { Tone } from "@custos/ui";

export type ActivityItem =
  | {
      kind: "deposit";
      id: string;
      at: string;
      chain: string;
      asset: string;
      amount: string;
      data: DepositInfo;
    }
  | {
      kind: "withdrawal";
      id: string;
      at: string;
      chain: string;
      asset: string;
      amount: string;
      data: WithdrawalInfo;
    }
  | {
      kind: "swap";
      id: string;
      at: string;
      chain: string;
      /** The asset given up (fromAsset); the asset filter matches either side. */
      asset: string;
      amount: string;
      data: SwapInfo;
    };

export interface ActivityFilter {
  type: "all" | "deposit" | "withdrawal" | "swap";
  /** "chain:SYMBOL" or "" for all assets. */
  asset: string;
  /** "YYYY-MM-DD" (local dates) or "". */
  from: string;
  to: string;
}

export const EMPTY_FILTER: ActivityFilter = { type: "all", asset: "", from: "", to: "" };

export function mergeActivity(
  deposits: DepositInfo[],
  withdrawals: WithdrawalInfo[],
  swaps: SwapInfo[] = [],
): ActivityItem[] {
  const items: ActivityItem[] = [
    ...deposits.map((d) => ({
      kind: "deposit" as const,
      id: d.id,
      at: d.detectedAt,
      chain: d.chain,
      asset: d.asset,
      amount: d.amount,
      data: d,
    })),
    ...withdrawals.map((w) => ({
      kind: "withdrawal" as const,
      id: w.id,
      at: w.requestedAt,
      chain: w.chain,
      asset: w.asset,
      amount: w.amount,
      data: w,
    })),
    ...swaps.map((sw) => ({
      kind: "swap" as const,
      id: sw.id,
      at: sw.createdAt,
      chain: sw.chain,
      asset: sw.fromAsset,
      amount: sw.fromAmount,
      data: sw,
    })),
  ];
  return items.sort(byNewest);
}

/** Newest first; items with the same time are ordered by kind, then id, so the list never reshuffles. */
function byNewest(a: ActivityItem, b: ActivityItem): number {
  return b.at.localeCompare(a.at) || a.kind.localeCompare(b.kind) || a.id.localeCompare(b.id);
}

/** Local midnight of a "YYYY-MM-DD" date. */
function localDate(day: string, endOfDay: boolean): number {
  const [y, m, d] = day.split("-").map(Number);
  return endOfDay ? new Date(y!, m! - 1, d! + 1).getTime() : new Date(y!, m! - 1, d!).getTime();
}

export function filterActivity(items: ActivityItem[], f: ActivityFilter): ActivityItem[] {
  const from = f.from ? localDate(f.from, false) : -Infinity;
  const to = f.to ? localDate(f.to, true) : Infinity;
  return items.filter((i) => {
    if (f.type !== "all" && i.kind !== f.type) return false;
    if (f.asset) {
      const sides = i.kind === "swap" ? [i.data.fromAsset, i.data.toAsset] : [i.asset];
      if (!sides.some((a) => `${i.chain}:${a}` === f.asset)) return false;
    }
    const t = Date.parse(i.at);
    return t >= from && t < to;
  });
}

export const CANCELLABLE: WithdrawalStatus[] = ["REQUESTED", "PENDING_APPROVAL", "QUEUED"];

/** Plain-English status labels. */
export function depositStatus(d: DepositInfo): { label: string; tone: Tone; help: string } {
  const map: Record<DepositStatus, { label: string; tone: Tone; help: string }> = {
    DETECTED: { label: "Detected", tone: "primary", help: "Seen on the network, waiting for confirmations." },
    CONFIRMING: {
      label: `Confirming ${d.confirmations}/${d.requiredConfirmations}`,
      tone: "primary",
      help: "Added to your balance once fully confirmed.",
    },
    CREDITED: { label: "Credited", tone: "success", help: "Added to your balance." },
    ORPHANED: {
      label: "Dropped",
      tone: "danger",
      help: "The transaction disappeared from the network before confirming.",
    },
    DUST: {
      label: "Below minimum",
      tone: "warning",
      help: "Smaller than the minimum deposit, so it wasn't credited.",
    },
  };
  return map[d.status];
}

export function withdrawalStatus(w: WithdrawalInfo): { label: string; tone: Tone; help: string } {
  const map: Record<WithdrawalStatus, { label: string; tone: Tone; help: string }> = {
    REQUESTED: {
      label: "Confirm by email",
      tone: "warning",
      help: "Open the link in the email we sent you to continue.",
    },
    PENDING_APPROVAL: {
      label: "Awaiting approval",
      tone: "warning",
      help: "An admin checks larger withdrawals before they are sent.",
    },
    QUEUED: { label: "Queued", tone: "primary", help: "Will be signed and sent shortly." },
    SIGNED: { label: "Signed", tone: "primary", help: "Signed, being sent to the network." },
    BROADCAST: {
      label: `Sent · ${w.confirmations} conf.`,
      tone: "primary",
      help: "On the network, waiting for confirmations.",
    },
    CONFIRMED: { label: "Completed", tone: "success", help: "Confirmed on the network." },
    FAILED: {
      label: "Failed · refunded",
      tone: "danger",
      help: "It didn't go through; the amount and fee were returned to your balance.",
    },
    REJECTED: {
      label: "Rejected · refunded",
      tone: "danger",
      help: "Rejected by an admin; the amount and fee were returned.",
    },
    CANCELLED: { label: "Cancelled", tone: "neutral", help: "Cancelled; the amount and fee were returned." },
  };
  return map[w.status];
}

export function swapStatus(sw: SwapInfo): { label: string; tone: Tone; help: string } {
  const testnet = sw.simulated ? " Testnet: only your account balances changed — no on-chain trade." : "";
  const map: Record<SwapStatus, { label: string; tone: Tone; help: string }> = {
    QUOTED: { label: "Quoted", tone: "neutral", help: "Not confirmed." },
    EXECUTING: { label: "In progress", tone: "primary", help: "Being carried out." },
    COMPLETED: { label: "Completed", tone: "success", help: `Swapped in your account.${testnet}` },
    FAILED: { label: "Failed · refunded", tone: "danger", help: "It didn't go through; nothing was taken." },
    EXPIRED: { label: "Expired", tone: "neutral", help: "The quote ran out before it was confirmed." },
  };
  return map[sw.status];
}
